/**
 * Application Update (Awaiting reasons) dialog
 * Shown when applicant status is AWAITING or VOID after loading application.
 * Informational only – OK to dismiss. Submit for Processing is in the header.
 * Matches Angular awaiting-reason component.
 */

import { OnScreenModal } from '@/components/ui/OnScreenModal'
import modalStyles from '@/components/ui/modals.module.css'
import type { AwaitingReasonDialogProps } from '@/features/applicant/types/components'

export function AwaitingReasonDialog({
  open,
  onClose,
  applicationData,
}: AwaitingReasonDialogProps) {
  const awaitingData = applicationData?.awaiting_data
  const awaitingNotes = applicationData?.awaiting_notes

  return (
    <OnScreenModal
      isOpen={open}
      title="Application Update"
      onClose={onClose}
      maxWidth="max-w-xl"
      headerVariant="centeredTitle"
      bodyScrollOnly
      overlayDataTestId="applicant-modal-awaiting-reason"
      panelDataTestId="applicant-modal-awaiting-reason-panel"
      titleDataTestId="applicant-modal-awaiting-reason-title"
      closeButtonDataTestId="applicant-modal-awaiting-reason-close-btn"
      bodyClassName="px-5 py-4 sm:px-6"
      footer={
        <div className={modalStyles.modalFooterActionGroup}>
          <button
            type="button"
            onClick={onClose}
            data-testid="applicant-modal-awaiting-reason-ok-btn"
            className={modalStyles.modalActionPrimary}
          >
            Ok
          </button>
        </div>
      }
    >
      <p className="mb-4 text-sm leading-relaxed text-[#232946]">
        Your application has been reset to an Awaiting status, so that you can update your information. When you have completed your updates, click the Submit for Processing button to continue.
      </p>
      {awaitingData && typeof awaitingData === 'object' && Object.keys(awaitingData).length > 0 && (
        <>
          <p className="text-sm text-gray-700 mb-4">
            Please review bullets below to see what we are waiting for :
          </p>
          <div className="mb-4 space-y-5">
            {Object.entries(awaitingData).map(([key, category]) => {
              const label = category?.label ?? key
              const children = category?.child ?? []
              return (
                <div key={key} className="space-y-2">
                  <div
                    className="font-semibold text-gray-900 text-[15px] awaiting-reason-html"
                    dangerouslySetInnerHTML={{ __html: label }}
                  />
                  {children.length > 0 && (
                    <div className="ml-4 space-y-3 mt-2 pl-0">
                      {children.map((child, idx) => {
                        const extra = typeof child?.inputvalue === 'string' ? child.inputvalue.trim() : ''
                        const personalizedMessage =
                          typeof child?.personalized_message === 'string'
                            ? child.personalized_message.trim()
                            : ''
                        return (
                          <div
                            key={child?.label ? `child-${child.label}-${idx}` : `child-${idx}`}
                            className="flex gap-2.5 text-sm text-gray-700 leading-relaxed"
                          >
                            <span className="shrink-0 text-gray-600 select-none" aria-hidden>
                              ◦
                            </span>
                            <div className="flex-1 min-w-0 space-y-1.5">
                              <div
                                className="awaiting-reason-html"
                                dangerouslySetInnerHTML={{
                                  __html: child?.label || '',
                                }}
                              />
                              {extra ? (
                                <p className="text-sm text-gray-600 leading-relaxed pl-0.5">
                                  {extra}
                                </p>
                              ) : null}
                              {personalizedMessage ? (
                                <p className="text-sm text-gray-900 leading-relaxed italic pl-0.5 whitespace-pre-line">
                                  <strong>Please note:</strong> {personalizedMessage}
                                </p>
                              ) : null}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
          {awaitingNotes ? (
            <div className="space-y-2">
              <div className="font-semibold text-gray-900 text-[15px]">Additional Message</div>
              <div className="flex gap-2.5 text-sm text-gray-700 leading-relaxed ml-4 mt-2">
                <span className="shrink-0 text-gray-600 select-none" aria-hidden>
                  ◦
                </span>
                <div
                  className="awaiting-reason-html flex-1 min-w-0"
                  dangerouslySetInnerHTML={{ __html: awaitingNotes }}
                />
              </div>
            </div>
          ) : null}
        </>
      )}
    </OnScreenModal>
  )
}
